import { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { usePermission } from 'admin/hocs'
import { useProductDetailTabs } from 'products/productDetailTabsContext'

export type ProductDetailSection = 'details' | 'validity' | 'pricing'

export function productDetailPagePath(
    target: ProductDetailSection,
    accountSlug: string,
    productId: string,
    viewMode: 'flat' | 'nested' | '',
) {
    const paths = {
        details: `/account/${accountSlug}/products/crud/${productId}`,
        validity: `/account/${accountSlug}/products/validity/${productId}/detail`,
        pricing: `/account/${accountSlug}/products/pricing/${productId}/detail`,
    }
    return `${paths[target]}?from=${viewMode}`
}

interface ProductDetailNavigationProps {
    accountSlug: string
    current: ProductDetailSection
    productId?: string
    title?: string
    viewMode?: 'flat' | 'nested' | ''
    onNavigate?: (target: ProductDetailSection) => void
}

function ProductDetailNavigation({
    accountSlug,
    current,
    productId,
    title,
    viewMode,
    onNavigate,
}: ProductDetailNavigationProps) {
    const history = useHistory()
    const { hasPermission } = usePermission()
    const { setDetailTabs, setDetailTitle } = useProductDetailTabs()

    const hrefFor = (target: ProductDetailSection) =>
        productId ? productDetailPagePath(target, accountSlug, productId, viewMode || '') : undefined

    const navigateRef = useRef<(target: ProductDetailSection) => void>(() => {})
    navigateRef.current =
        onNavigate ||
        ((target: ProductDetailSection) =>
            history.push(productDetailPagePath(target, accountSlug, productId || '', viewMode || '')))

    const canEditDetails = hasPermission('partner_admin', accountSlug)
    const canViewValidity = hasPermission('view_product_validity', accountSlug)
    const canViewPricing = hasPermission('view_pricing_settings', accountSlug)

    useEffect(() => {
        setDetailTabs(
            [
                {
                    name: 'Details',
                    href: hrefFor('details'),
                    onClick: () => navigateRef.current('details'),
                    current: current === 'details',
                    disabled: !canEditDetails,
                    disabledReason: 'You do not have permission to edit product details',
                },
                {
                    name: 'Validity',
                    href: hrefFor('validity'),
                    onClick: () => navigateRef.current('validity'),
                    current: current === 'validity',
                    disabled: !canViewValidity,
                    disabledReason: 'You do not have permission to view validity settings',
                },
                {
                    name: 'Pricing',
                    href: hrefFor('pricing'),
                    onClick: () => navigateRef.current('pricing'),
                    current: current === 'pricing',
                    disabled: !canViewPricing,
                    disabledReason: 'You do not have permission to view pricing settings',
                },
            ],
            { showBackToProducts: true },
        )

        return () => setDetailTabs(null)
    }, [setDetailTabs, current, canEditDetails, canViewValidity, canViewPricing])

    useEffect(() => {
        setDetailTitle(title || '')
        return () => setDetailTitle('')
    }, [title, setDetailTitle])

    return null
}

export default ProductDetailNavigation
